import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import Layout from '../components/Layout';
import { lazy, Suspense } from 'react';
import AuthLayout from '../components/Layout/auth';
import { ProtectedRoute } from '../components/ProtectedRoute';
import LecturerLayout from '../components/Layout/lecturer';
import { useAuth } from '../contexts/AuthContext';

const Login = lazy(() => import('../pages/Auth'));

const Home = lazy(() => import('../pages/Home'));

const Users = lazy(() => import('../pages/Users'));
const EditUser = lazy(() => import('../pages/Users/edit'));

const Students = lazy(() => import('../pages/Students'));
const AddStudent = lazy(() => import('../pages/AddEditStudent'));
const StudentEdit = lazy(() => import('../pages/AddEditStudent/edit'));
const StudentLogs = lazy(() => import('../pages/StudentLogs'));

const Measurement = lazy(() => import('../pages/Setting/measurement'));
const EditMeasurement = lazy(() => import('../pages/Setting/measurement/edit'));
const Intermediary = lazy(() => import('../pages/Setting/intermediary'));
const EditIntermediary = lazy(() => import('../pages/Setting/intermediary/edit'));
const Examinations = lazy(() => import('../pages/Setting/examinations'));
const BridgeDays = lazy(() => import('../pages/Setting/bridge-days'));

const AssessmentOfSuitability = lazy(() => import('../pages/Documents/assessment-suitability'));
const AssessmentOfSuitabilityForm = lazy(() => import('../pages/Documents/assessment-suitability-form'));
const DataCollectionForm = lazy(() => import('../pages/Documents/data-collection-form'));
const DataCollection = lazy(() => import('../pages/Documents/data-collection'));

const Lecturers = lazy(() => import('../pages/Lecturers'));

const FeedbackForm = lazy(() => import('../pages/Carrier/feedback-form'));
const SuccessPlacementStatistics = lazy(() => import('../pages/Carrier/success-placement-statistics'));
const QualificationPlan = lazy(() => import('../pages/Carrier/qualification-plan'));
const Documents = lazy(() => import('../pages/Carrier/documents'));
const Organization = lazy(() => import('../pages/Carrier/organization'));
const QualificationMatrix = lazy(() => import('../pages/Carrier/qualification-matrix'));

const LiveAttendance = lazy(() => import('../pages/Attendance/live'));
const MissingSignatures = lazy(() => import('../pages/Attendance/missing-signatures'));


const TrainingReports = lazy(() => import('../lecturer-pages/TrainingReports'));

const LecturerStudents = lazy(() => import('../lecturer-pages/Students'));
const LecturerLiveAttendance = lazy(() => import('../lecturer-pages/Attendance/live'));
const LecturerAddEditStudent = lazy(() => import('../lecturer-pages/AddEditStudent'));
const LecturerDocuments = lazy(() => import('../lecturer-pages/Documents'));
const LecturerLiveWebinar = lazy(() => import('../lecturer-pages/Live-Webinar'));
const LecturerStundenplan = lazy(() => import('../lecturer-pages/Stundenplan'));

export const super_admin = ["/students/logs"]

const AppRouter = () => {
    const { auth } = useAuth()
    const LoginPage = () => {
        return (
            <AuthLayout>
                <Suspense fallback={null}>
                    <Login />
                </Suspense>
            </AuthLayout>
        )
    }

    const routerAuth = createBrowserRouter([{ path: '/', element: <LoginPage /> }, { path: '*', element: <Navigate to="/" /> },])

    const routerAdmin = createBrowserRouter([
        { path: '/login', element: <LoginPage /> },
        {
            element: (
                <ProtectedRoute>
                    <Layout />
                </ProtectedRoute>
            ),
            children: [
                {
                    path: '/',
                    element: <Home />,

                },
                {
                    path: '/users',
                    element: <Users />,
                },
                {
                    path: '/users/:id',
                    element: <EditUser />
                },
                {
                    path: '/students',
                    element: <Students />
                },
                {
                    path: '/students/logs',
                    element: <StudentLogs />
                },
                {
                    path: '/add-student',
                    element: <AddStudent />
                },
                {
                    path: '/edit-student',
                    element: <StudentEdit />
                },
                {
                    path: '/settings/measurement',
                    element: <Measurement />
                },
                {
                    path: '/settings/measurement/edit',
                    element: <EditMeasurement />
                },
                {
                    path: '/settings/intermediary',
                    element: <Intermediary />
                },
                {
                    path: '/settings/intermediary/edit',
                    element: <EditIntermediary />
                },
                {
                    path: '/settings/examinations',
                    element: <Examinations />
                },
                {
                    path: '/settings/bridge-days',
                    element: <BridgeDays />
                },
                {
                    path: '/documents/assessment-suitability',
                    element: <AssessmentOfSuitability />
                },
                {
                    path: '/documents/data-collection',
                    element: <DataCollection />
                },
                {
                    path: '/lecturers',
                    element: <Lecturers />
                },
                {
                    path: '/carrier/feedback-form',
                    element: <FeedbackForm />
                },
                {
                    path: '/carrier/success-and-placement-statistics',
                    element: <SuccessPlacementStatistics />
                },
                {
                    path: '/carrier/qualification-plan',
                    element: <QualificationPlan />
                },
                {
                    path: '/carrier/documents',
                    element: <Documents />
                },
                {
                    path: '/carrier/organization',
                    element: <Organization />
                },
                {
                    path: '/carrier/qualification-matrix',
                    element: <QualificationMatrix />
                },
                {
                    path: '/attendance/live',
                    element: <LiveAttendance />
                },
                {
                    path: '/attendance/missing-signatures',
                    element: <MissingSignatures />
                },
            ]
        },
        {
            element: (<Layout normal={true} />),
            children: [
                {
                    path: '/documents/assessment-suitability-form',
                    element: <AssessmentOfSuitabilityForm />
                },
                {
                    path: '/documents/data-collection-form',
                    element: <DataCollectionForm />
                },
            ]
        },
        {
            path: '*',
            element: <h1 style={{ textAlign: 'center' }}>404 Error - Nothing here...</h1>
        }
    ]);

    const routerLecturer = createBrowserRouter([
        { path: '/login', element: <LoginPage /> },
        {
            element: (
                <ProtectedRoute>
                    <LecturerLayout />
                </ProtectedRoute>
            ),
            children: [
                {
                    path: '/',
                    element: <TrainingReports />,
                },
                {
                    path: '/students',
                    element: <LecturerStudents />,
                },
                {
                    path: '/attendance/live',
                    element: <LecturerLiveAttendance />,
                },
                {
                    path: '/lecturer-students',
                    element: <LecturerAddEditStudent />,
                },
                {
                    path: '/live-webinar',
                    element: <LecturerLiveWebinar />,
                },
                {
                    path: '/documents',
                    element: <LecturerDocuments />,
                },
                {
                    path: '/stundenplan',
                    element: <LecturerStundenplan />,
                },
            ]
        },
        {
            path: '*',
            element: <h1 style={{ textAlign: 'center' }}>404 Error - Nothing here...</h1>
        }
    ]);

    if (!auth?.isAuthenticated) {
        return <RouterProvider key="auth-router-provider" router={routerAuth} />;
    }

    if (auth?.role === "lecturer") {
        return <RouterProvider key="lecturer-router-provider" router={routerLecturer} />;
    }

    return <RouterProvider key="admin-router-provider" router={routerAdmin} />;
};

export default AppRouter;